import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import { academyRegistrationTranslations } from '../i18n/translations';

const FUNCTIONS_BASE_URL = 'https://us-central1-mendyx-adafe.cloudfunctions.net';

const functionUrl = (name, params = {}) => {
  const url = new URL(`${FUNCTIONS_BASE_URL}/${name}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, value);
    }
  });
  return url.toString();
};

const postFunctionJson = async (name, body) => {
  const response = await fetch(`${FUNCTIONS_BASE_URL}/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data.error || 'Erreur serveur');
    error.data = data;
    error.status = response.status;
    throw error;
  }
  return data;
};

const CourseRegistration = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = createTranslator(language, academyRegistrationTranslations);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    phone: '',
    email: '',
    niveau: '',
    newsletter: false,
    consentInfo: false,

  });
  const [limitReached, setLimitReached] = useState(false);
  const [courseData, setCourseData] = useState(null);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        const response = await fetch(
          functionUrl('protectedGetCourseRegistrationPublicDetails', { courseId })
        );
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Erreur serveur');
        }

        setCourseData(data);
        setLimitReached(Boolean(data.limitReached));
      } catch (error) {
        console.error('Erreur chargement du cours :', error);
      }
    };
    fetchCourseData();
  }, [courseId]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.newsletter) {
      alert(t('validation.acceptNewsletter'));
      return;
    }

    try {
      // Ajouter l'inscription à Firestore
      await postFunctionJson('protectedCreateCourseRegistrationPublic', {
        courseId,
        ...formData,
        language,
      });

      // Envoyer les données au Cloud Function pour l'email
      await fetch(
        'https://us-central1-mendyx-adafe.cloudfunctions.net/sendCourseEmail',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            courseId,
            ...formData,
            language,
          }),
        }
      );

      // Rediriger vers la page de succès
      navigate('/success-course');
    } catch (error) {
      console.error('Erreur lors de l\'inscription :', error);
      alert(t('validation.genericError'));
    }
  };

  return (
    <section className="container mx-auto my-16 px-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-stretch min-h-[700px]">
        <div className="flex justify-center items-center">
          <div className="relative w-full h-full overflow-hidden rounded-lg shadow-lg">
            <img
              src={courseData?.picture}
              alt={`Cours ${courseId}`}
              className="object-contain w-full h-full transition-transform duration-300 hover:scale-105"
            />
          </div>
        </div>
        <div className="p-6 bg-white shadow-lg rounded-r-lg transition-transform duration-300 hover:scale-105">
          <h2 className="text-2xl font-semibold text-[#4c2f8e] mb-4">{t('academy.courseRegistrationTitle')}</h2>
          {limitReached ? (
            <p className="text-red-500">{t('validation.maxParticipantsReached')}</p>
          ) : (
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div className="flex flex-col">
                <label className="text-gray-700">{t('forms.firstName')}</label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className="p-2 border rounded-md"
                  required
                />
              </div>
              <div className="flex flex-col">
                <label className="text-gray-700">{t('forms.lastName')}</label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className="p-2 border rounded-md"
                  required
                />
              </div>
              <div className="flex flex-col">
                <label className="text-gray-700">{t('forms.phone')}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="p-2 border rounded-md"
                  required
                />
              </div>
              <div className="flex flex-col">
                <label className="text-gray-700">{t('forms.emailAddress')}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="p-2 border rounded-md"
                  required
                />
              </div>
              <div className="flex flex-col">
                <label className="text-gray-700">{t('forms.level')}</label>
                <input
                  type="text"
                  name="niveau"
                  value={formData.niveau}
                  onChange={handleChange}
                  className="p-2 border rounded-md"
                  required
                />
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="newsletter"
                  checked={formData.newsletter}
                  onChange={handleChange}
                  className="h-4 w-4 text-[#4c2f8e] border-gray-300 rounded"
                  required
                />
                <label className="ml-2 text-gray-700">
                  {t('forms.infoAndNewsletterConsent')}
                </label>
              </div>
              <button
                type="submit"
                className="mt-6 w-full px-6 py-3 bg-gradient-to-r from-[#4c2f8e] to-[#ea4c89] text-white rounded-full shadow-lg transform transition-all duration-300 hover:scale-110 hover:shadow-2xl"
              >
                {t('tournaments.register')}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default CourseRegistration;
